import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Flowvea cart drawer actions: quantity, remove and upgrade to the next bundle.
 * Each action asks the cart for the drawer section's new HTML, then fires the theme's
 * cart update event so Horizon's <cart-items-component> morphs it in and the header
 * count updates.
 */
const ROOT = window.Shopify?.routes?.root ?? '/';
const SECTION_ID = 'cart-drawer-section';

/**
 * @param {string} path
 * @param {Record<string, unknown>} body
 * @param {boolean} [withSections]
 */
async function post(path, body, withSections = true) {
  const payload = withSections ? { ...body, sections: SECTION_ID, sections_url: window.location.pathname } : body;
  const response = await fetch(`${ROOT}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  });
  const result = await response.json();
  if (!response.ok || result.status) {
    throw new Error(result.description || result.message || 'Something went wrong. Please try again.');
  }
  return result;
}

/** @param {string} keys */
const toUpdates = (keys) =>
  Object.fromEntries(
    keys
      .split(',')
      .map((key) => key.trim())
      .filter(Boolean)
      .map((key) => [key, 0])
  );

/**
 * @param {HTMLElement} source
 * @param {Record<string, string> | undefined} sections
 * @param {{ id: string, quantity: number }[]} lines
 */
async function publish(source, sections, lines) {
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines, promise: deferred.promise })
  );
  const cart = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: { items: cart.items, source: 'rn-cart', sections, didError: false },
  });
}

document.addEventListener('click', async (event) => {
  if (!(event.target instanceof Element)) return;
  const button = event.target.closest('[data-rn-cart-action]');
  const root = button?.closest('[data-rn-cart]');
  if (!(button instanceof HTMLElement) || !(root instanceof HTMLElement) || root.classList.contains('is-busy')) return;
  event.preventDefault();

  const error = root.querySelector('[data-rn-cart-error]');
  if (error instanceof HTMLElement) error.hidden = true;
  root.classList.add('is-busy');

  const { rnCartAction: action, key = '', keys = '', quantity, items } = button.dataset;
  try {
    let result;
    let lines;

    if (action === 'change') {
      const qty = Number(quantity);
      result = await post('cart/change.js', { id: key, quantity: qty });
      lines = [{ id: key, quantity: qty }];
    } else if (action === 'remove') {
      const updates = toUpdates(keys);
      result = await post('cart/update.js', { updates });
      lines = Object.keys(updates).map((id) => ({ id, quantity: 0 }));
    } else if (action === 'upgrade') {
      const updates = toUpdates(keys);
      await post('cart/update.js', { updates }, false);
      const added = JSON.parse(items || '[]');
      result = await post('cart/add.js', { items: added });
      lines = added.map((item) => ({ id: String(item.id), quantity: item.quantity }));
    } else {
      return;
    }

    await publish(button, result.sections, lines);
  } catch (err) {
    // An upgrade may have removed the old line before the add failed: re-sync the drawer
    if (action === 'upgrade') publish(root, undefined, [{ id: key || 'upgrade', quantity: 0 }]).catch(() => {});
    if (error instanceof HTMLElement) {
      error.textContent = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      error.hidden = false;
    }
  } finally {
    root.classList.remove('is-busy');
  }
});
